// Angular imports
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Params, Router } from '@angular/router';

// 3rd party imports
import notify from 'devextreme/ui/notify';

// Rxjs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// CC-Common imports
import { Utils } from 'cc-common';

// Service imports
import { UserService } from '../_services/data-services/user.service';

// Model imports
import { SideNavTreeInfo } from '../_models/side-nav-tree-info';
import { ActionAccessListParameterDto } from '../_models/action-access-list-parameter-dto';
import { ActionAccessResultDto } from '../_models/action-access-result-dto';

// Constant imports
import { MessageInterval } from '../_constants/messages-intervals';
import { SideNavigationTree } from '../_constants/side-navigation-tree';

@Injectable({
    providedIn: 'root'
})
export class AccessGuard implements CanActivate {
    menuItems: SideNavTreeInfo[];
    accessCode: string;
    routeParams: Params;

    constructor(private readonly router: Router, private readonly userService: UserService) {
        const treeItems = []
            .concat(SideNavigationTree.bottomMenuItems)
            .concat(SideNavigationTree.topMenuItems)
            .concat(SideNavigationTree.communityMenuItems)
            .concat(SideNavigationTree.propertyMenuItems)
            .concat(SideNavigationTree.hiddenPropertyItems);
        this.menuItems = SideNavigationTree.flattenSideNavTreeItems(treeItems).filter((item) =>
            Utils.notNullAndDefined(item.path)
        );
    }

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        // const isBoardMember = this.userService.isBoardMember();
        // if (Utils.isTrue(isBoardMember)) {
        //     window.location.href = environment.boardMemberRedirectionUrl;
        //     return of(false);
        // } else {
        this.accessCode = null;
        this.routeParams = {};
        if (Utils.notNullAndDefined(route.data) && Utils.notNullAndDefined(route.data.accessCode)) {
            this.accessCode = route.data.accessCode.toUpperCase();
            Object.assign(this.routeParams, route.params);
        } else {
            this.findAccessCodeFromRoute(route);
        }
        return this.hasActionAccess();
        // }
    }

    private hasActionAccess(): Observable<boolean> {
        return this.getActionAccessCodes().pipe(
            map((actionItems: string[]) => {
                const hasAccess =
                    Utils.notNullAndDefined(this.accessCode) && Utils.isInList(this.accessCode, actionItems);
                if (Utils.isFalse(hasAccess) && Utils.notNullOrEmpty(actionItems)) {
                    this.router.navigate(['home']);
                    notify('Invalid route. Redirecting to Home', 'error', MessageInterval.defaultErrorMsgInterval);
                }
                return Utils.isTrue(hasAccess || Utils.isNullOrEmpty(actionItems));
            })
        );
    }

    private getActionAccessCodes(): Observable<string[]> {
        const params: ActionAccessListParameterDto = new ActionAccessListParameterDto();
        params.communityId = this.routeParams.communityId;
        params.customerId = this.routeParams.customerId;
        return this.userService
            .getActionList(params)
            .pipe(map((results: ActionAccessResultDto[]) => results.map((r) => r.code.toUpperCase())));
    }

    private findAccessCodeFromRoute(route: ActivatedRouteSnapshot): void {
        // Populate route url and route name from complete route hierarchy
        let routeUrl = route.pathFromRoot
            .filter((p) => Utils.notNullOrEmpty(p.url))
            .map((p) => {
                Object.assign(this.routeParams, p.params);
                return p.url.map((us) => us.path).join('/');
            })
            .join('/');
        if (Utils.containsIgnoreCase(routeUrl, '?')) {
            routeUrl = routeUrl.split('?')[0];
        }
        let routeName = routeUrl;
        if (Utils.startsWithIgnoreCase(routeUrl, '/')) {
            routeName = routeUrl.slice(1);
        } else {
            routeUrl = `/${routeUrl}`;
        }
        // Find menu item based on route url or route name to get access code from menu config
        const menuItem = this.menuItems.find((info: SideNavTreeInfo) => {
            return (
                Utils.equalsIgnoreCase(this.toContextPath(info.path), routeUrl) ||
                Utils.equalsIgnoreCase(info.name, routeName)
            );
        });
        if (!!menuItem) {
            this.accessCode = menuItem.name.toUpperCase();
        }
    }

    private toContextPath(path: string): string {
        let contextPath = Utils.notNullAndDefined(path) ? path : '';
        if (Utils.containsIgnoreCase(contextPath, '?')) {
            contextPath = contextPath.split('?')[0];
        }
        if (Utils.isFalse(Utils.startsWithIgnoreCase(contextPath, '/'))) {
            contextPath = `/${contextPath}`;
        }
        const communityId = this.routeParams.communityId;
        const customerId = this.routeParams.customerId;
        if (Utils.notNullAndDefined(customerId)) {
            contextPath = contextPath.replace(':customerId', customerId);
        } else {
            contextPath = contextPath.replace('/:customerId', '');
        }
        if (Utils.notNullAndDefined(communityId)) {
            contextPath = contextPath.replace(':communityId', communityId);
        } else {
            contextPath = contextPath.replace('/:communityId', '');
        }
        return contextPath;
    }
}
